import type { ApiResponse, GetListResponse } from '@gokrew/models/generic';
import type { QuestionType } from '@gokrew/models/survey';
import api from './client';
import type {
  SurveyAnswerValue,
  EmployeeAnswerFile,
  EmployeeSurveyDetail,
  EmployeeSurveyDashboard,
  GetMySurveysParams,
  RawSurveyRecipient,
  RawEmployeeSurveyDetailResponse,
} from '@/types/employee-survey';
import {
  mapPageFromApi,
  collectQuestionTypes,
  mapSavedAnswerToValue,
} from '@/utils/employee-survey';
import { buildSaveAnswersPayload } from '@/utils/survey-answer-payload';

export async function getMySurveys(
  params: GetMySurveysParams
): Promise<GetListResponse<RawSurveyRecipient>> {
  const { status, page, limit } = params;
  const { data } = await api.get<GetListResponse<RawSurveyRecipient>>(
    '/employee/surveys',
    { params: { status, page, limit } }
  );
  return data;
}

export async function getEmployeeSurveyDashboard(): Promise<
  ApiResponse<EmployeeSurveyDashboard>
> {
  const { data } = await api.get<ApiResponse<EmployeeSurveyDashboard>>(
    '/employee/surveys/dashboard'
  );
  return data;
}

export async function getSurveyToTake(
  surveyId: string
): Promise<ApiResponse<EmployeeSurveyDetail>> {
  const { data } = await api.get<ApiResponse<RawEmployeeSurveyDetailResponse>>(
    `/employee/surveys/${surveyId}`
  );
  const raw = data.data;
  const pages = [...raw.survey.pages]
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map(mapPageFromApi);
  const questionTypes = collectQuestionTypes(pages);

  const savedAnswers: Record<string, SurveyAnswerValue> = {};
  const savedFiles: Record<string, EmployeeAnswerFile[]> = {};
  for (const answer of raw.savedAnswers) {
    if (answer.files.length > 0) {
      savedFiles[answer.questionId] = answer.files;
      continue;
    }
    savedAnswers[answer.questionId] = mapSavedAnswerToValue(
      questionTypes.get(answer.questionId),
      answer
    );
  }

  return {
    message: data.message,
    data: {
      assignmentId: raw.id,
      surveyId: raw.surveyId,
      status: raw.status,
      isReopened: raw.isReopened,
      estimatedMinutes: raw.survey.estDurationMin ?? 0,
      basicInfo: {
        title: raw.survey.title,
        description: raw.survey.description ?? undefined,
        instructions: raw.survey.instructions ?? undefined,
        isAnonymous: raw.survey.isAnonymous,
        availableUntil: raw.survey.endsOn ?? undefined,
      },
      savedAnswers,
      savedFiles,
      pages,
    },
  };
}

export async function startSurvey(surveyId: string): Promise<void> {
  await api.post(`/employee/surveys/${surveyId}/start`);
}

// `questionTypes`/`fileAnswers` let the caller (the hook) pass along what it
// already knows about each question — this module doesn't hold survey state.
// `savedQuestionIds` (what the server already holds) lets an emptied answer
// be sent as an explicit clear.
export async function saveSurveyAnswers(
  surveyId: string,
  answers: Record<string, SurveyAnswerValue>,
  questionTypes: Map<string, QuestionType>,
  fileAnswers: Record<string, EmployeeAnswerFile[]>,
  savedQuestionIds?: ReadonlySet<string>
): Promise<ApiResponse<void>> {
  const payload = buildSaveAnswersPayload(
    answers,
    questionTypes,
    fileAnswers,
    savedQuestionIds
  );

  if (payload.length === 0) {
    return { message: 'ok', data: undefined };
  }

  const { data } = await api.put<ApiResponse<void>>(
    `/employee/surveys/${surveyId}/answers`,
    { answers: payload }
  );
  return data;
}

export async function submitSurvey(
  surveyId: string
): Promise<ApiResponse<void>> {
  const { data } = await api.post<ApiResponse<void>>(
    `/employee/surveys/${surveyId}/submit`
  );
  return data;
}
